import { endOfMonth, isDate, isValid, startOfMonth } from "date-fns";
import React, { useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCSelectOptionsCard, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import UserColumn from "src/components/DefaultColumnsList/User";
import FiltersContainer from "src/components/FiltersContainer";
import List from "src/components/List";
import LovUsers from "src/components/Lov/Users";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import PageTitle from "src/components/PageTitle";
import Photo from "src/components/Photo";
import ScoreCalendar from "src/components/ScoreCalendar";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import formatDate from "src/utils/formatDate";
import isDateInBetween from "src/utils/isDateInBetween";
import stringToDate from "src/utils/stringToDate";
import { css } from "styled-components";
import ModalActions from "../Actions/Panel/components/modalAddChangeDelete";
import { ActionTraceability, IRefActionsPanelModalAddChangeDelete } from "../Actions/Panel/interface";
import ModalAudit from "../Audits/Panel/Components/ModalAudit";
import { IRefModalAuditAddOrChange } from "../Audits/Panel/Components/ModalAudit/interface";
import ModalFormMeeting from "../Meetings/Panel/components/modalFormMeeting";
import { FormMeetingAction, IRefModalFormMeeting } from "../Meetings/Panel/interface";
import ModalOcurrences from "../Occurrences/Panel/components/ModalAddOrChange";
import { IRefModalAddOrChange } from "../Occurrences/Panel/interface";
import { EventType, IFiltersCalendar, IScoreEvent } from "./interface";
import { AgendaContainer, CalendarFilterButton, EventContainer, FixedDiv, OverflowSpan } from "./styles";

const Calendar : React.FC = () => {
	const { translate,currentLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { user,administrator,superUser } = useAuth();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;

	const [showFilters, setShowFilters] = useState(false);
	const [myActions, setMyActions] = useState(false);
	const [filters, setFilters] = useState<IFiltersCalendar>({ 
		responsible: "",
		filter: "",
		startDate:formatDate(startOfMonth(new Date())),
		endDate:formatDate(endOfMonth(new Date()))
	});
	
	const [filterDate, setFilterDate] = useState<EventType | undefined>(undefined);
	const [date,setDate] = useState(new Date());
	const [displayEvents, setDisplayEvents] = useState<IScoreEvent[]>([]);
	const [events,setEvents] = useState<IScoreEvent[]>([]);
	const [modalEvents,setModalEvents] = useState<{date:Date,events:IScoreEvent[]}>();
	const [useEndDate, setUseEndDate] = useState(false);

	const refModalActions = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const refModalAudit = useRef<IRefModalAuditAddOrChange>(null);
	const refModalOcurrences = useRef<IRefModalAddOrChange>(null);
	const refModalMeeting = useRef<IRefModalFormMeeting>(null);
	const refModalEvents = useRef<RCCRefModalProps>(null);
	
	const { 
		register:registerFilters,
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		reset: resetFilters, 
		setValue: setValueFilters, 
		watch: watchFilters
	} = useForm();

	const areUsingFilter = ():boolean => !!filters.filter || !!filters.responsible;
	
	const HasPermission = (responsible:string) => administrator || superUser || responsible.toString() === user.efa005Id.toString();

	const eventTypeFilterHandle = (type: EventType) => {
		let typeToUpdate: EventType | undefined = type;

		if(filterDate && filterDate === type)
			typeToUpdate = undefined;
		
		setFilterDate(typeToUpdate);
	};

	const submitFilters = async (data: any) => {
		const dataForm = data;
	
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible.value;

		if(myActions)
			dataForm.responsible = user.efa005Id.toString();

		setFilters((prev) => ({...prev,...dataForm}));
	};

	const { loadData } = useEffectFetchData<IScoreEvent[]> (
		"Calendar/GetEvents", 
		{ 
			responsible: filters.responsible || "",
			filter: filters.filter,
			startDate:filters.startDate,
			endDate:filters.endDate,
			useEndDate
		 },
		 ({ data }) => {
			setEvents(data);
		 },
		 [filters],
		 true
	);

	const changeRangeHandle =async () => {
		const period = watchFilters("yearPicker");
		onChangePeriod(period);
	};

	const filterEventsCount = (eventType:EventType) => {
		if(eventType === EventType.REUNIONS){
			return events
				.filter((x) => x.eventType === eventType)
				.length;
		}
		return events
			.filter((x) => x.eventType === eventType)
			.filter((x) => {
				if (!useEndDate){
					return isDateInBetween(new Date(x.startDate), stringToDate(filters.startDate) as Date, stringToDate(filters.endDate) as Date);
				}
				return isDateInBetween(new Date(x.endDate), stringToDate(filters.startDate) as Date, stringToDate(filters.endDate) as Date);
			})
			.length;
	};
	
	const openEventModal = (event:any) => {
		switch (event.eventType) {

		case EventType.ACTION:
			refModalActions.current?.openModalAddOrChange(event.id,loadData,undefined,undefined,undefined,undefined,undefined,!HasPermission(event.responsible));
			break;

		case EventType.OCCURRENCE:
			refModalOcurrences.current?.actionButtonChange(event.id);
			break;

		case EventType.AUDIT:
			refModalAudit.current?.actionButtonChange(event.id);
			break;
			
		case EventType.REUNIONS:
			refModalMeeting.current?.actionButtonRequest(event.id, FormMeetingAction.VIEW, loadData);
			break;
			
		default:
			addToast({
				title: translate("Aviso"),
				description: translate("Evento não identificado"),
				color: "yellow"
			});
			break;
		}
	};

	const getTraceabilityMessage = (event:any) => {
		switch(event?.traceability?.toString()){
		case ActionTraceability.INICIATIVE_MAP:
			return `${translate("Iniciativa")} - ${translate("Mapa")}`;
		case ActionTraceability.FCA_INDICATOR:
			return `${translate("FCA")} - ${translate("Indicador")}`;
		case ActionTraceability.MEETING:
			return `${translate("Reunião")}`;
		case ActionTraceability.PROJECT:
			return `${translate("Projeto")}`;
		case ActionTraceability.RISK:
			return `${translate("Risco")}`;
		case ActionTraceability.OCCURRENCE:
			return `${translate("Ocorrência")}`;
		case ActionTraceability.INICIATIVE_OKR:
			return `${translate("Iniciativa")} - ${translate("OKR")}`;
		default:
			return "";
		}
	};

	const colTitle = (row:any) => (
		<>
			<OverflowSpan title={row?.title}>{row?.title}</OverflowSpan>
			<FixedDiv type={row?.eventType}/>
		</>
	);

	const colUser = (row:any) => (
		<>
			{ (row.responsibleName && !myActions) && 
				<UserColumn 
					name={row.responsibleName}
					image={row.responsibleImage}
				/>
			}
		</>
	);	

	const colRastreability = (row:any) => {
		const traceabilityMessage = getTraceabilityMessage(row);
		return (
			<>
				{traceabilityMessage && (
					<RCCBadge 
						color="grey" 
						styleInline={css`
						padding: 1px 5px; 
						font-size: 0.7rem;
					`}>
						{traceabilityMessage}
					</RCCBadge>
				)}
			</>
		);
	};

	useEffectSkipInitialRender(() => {
		setDisplayEvents(
			events.map((x) => {
				let start; 
				let end;
				if(x.eventType === EventType.REUNIONS){
					start = new Date(x.startDate);
					end = new Date(x.endDate);
				}else{
					start = new Date(!useEndDate ? x.startDate : x.endDate);
					end = new Date(!useEndDate ? x.startDate : x.endDate);
				}
				
				return {
					...x,
					start,
					end,
					allDay:x.eventType !== EventType.REUNIONS
				};
			}
			).filter((x) => filterDate ? x.eventType === filterDate : true)
		);
	}, [events,useEndDate,filterDate]);

	const onChangePeriod = (newDate:Date) => {
		setEvents([]);

		if(!isDate(newDate) || !isValid(newDate))
			return;

		setDate(newDate);
		const startDate = formatDate(startOfMonth(newDate));
		const endDate = formatDate(endOfMonth(newDate));
		setFilters((prevState) => ({
			...prevState,
			startDate,
			endDate
		}));
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				<PageTitle
					title={translate("Calendário")}
					icon={{
						name: "calendar"
					}}
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
				<FiltersContainer
					showFilters={showFilters}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => {
						resetFilters({ 
							responsible: { value: "", label: ""},
							filter: ""
						});
					}}
				>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
						>
							<LovUsers 
								control={controlFilters}
								name="responsible"
								label={translate("Responsável")}
								defaultValue={filters.responsible}
								disabled={myActions}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
						>
							<RCCInput
								name="filter" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.filter}
							/>
						</RCCGrid>
					</RCCGrid>

				</FiltersContainer>	
				<RCCGrid container>
					<RCCGrid
						container
						item
						spacing={1}
						justifyContent="space-between"
					>
						<RCCGrid container item
							xs={12}
							sm={6}
							md={2}
						>
							<DateTimePicker
								control={controlFilters}
								label={translate("Período")}
								name="yearPicker"
								type="datePicker"
								props={{
									datePicker:{
										format:"MM/yyyy",
										views:["month"]
									}
								}}
								onChange={onChangePeriod}
								defaultValue={date}
							/>
						</RCCGrid>
						<RCCGrid
							item
						>
							<RCCSelectOptionsCard
								defaultValue={useEndDate ? "F" : "I"}
								options={[{
									value: "I",
									label: translate("Mostrar datas iniciais")
								},{
									value: "F",
									label: translate("Mostrar datas finais")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setUseEndDate(e === "F");
								}}
							/>
						</RCCGrid>
						<RCCGrid
							item
							styleInline="display:flex; align-items: flex-end;"
						>
							{filterEventsCount(EventType.ACTION) > 0 && (
								<CalendarFilterButton 
									color="purple" 
									isActive={!!(filterDate && filterDate !== EventType.ACTION)}
									onClick={(e) => { e.stopPropagation(); eventTypeFilterHandle(EventType.ACTION); }}
								>
									{filterEventsCount(EventType.ACTION)} {translate("Ações")}
								</CalendarFilterButton>
							)}
							{filterEventsCount(EventType.OCCURRENCE) > 0 && (
								<CalendarFilterButton 
									color="blue" 
									isActive={!!(filterDate && filterDate !== EventType.OCCURRENCE)}
									onClick={(e) => { e.stopPropagation(); eventTypeFilterHandle(EventType.OCCURRENCE); }}
								>
									{filterEventsCount(EventType.OCCURRENCE)} {translate("Ocorrências")}
								</CalendarFilterButton>
							)}
							{filterEventsCount(EventType.AUDIT) > 0 && (
								<CalendarFilterButton 
									color="orange" 
									isActive={!!(filterDate && filterDate !== EventType.AUDIT)}
									onClick={(e) => { e.stopPropagation(); eventTypeFilterHandle(EventType.AUDIT); }}
								>
									{filterEventsCount(EventType.AUDIT)} {translate("Auditorias")}
								</CalendarFilterButton>
							)}
							{filterEventsCount(EventType.REUNIONS) > 0 && (
								<CalendarFilterButton 
									color="green" 
									isActive={!!(filterDate && filterDate !== EventType.REUNIONS)}
									onClick={(e) => { e.stopPropagation(); eventTypeFilterHandle(EventType.REUNIONS); }}
								>
									{filterEventsCount(EventType.REUNIONS)} {translate("Reuniões")}
								</CalendarFilterButton>
							)}
						</RCCGrid>
						<RCCGrid
							item
						>
							<RCCSelectOptionsCard
								defaultValue={myActions ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todos eventos")
								},{
									value: "S",
									label: translate("Meus Eventos")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsible", { value: "", label: ""});
									if(e === "S"){
										setMyActions(true);
										setFilters({
											...filters,
											responsible: user.efa005Id.toString()
										});
										return;
									}
									setMyActions(false);
									setFilters({
										...filters,
										responsible: ""
									});
								}}
							/>
						</RCCGrid>
						
					</RCCGrid>
				</RCCGrid>	 
				<ScoreCalendar
					events={displayEvents}
					date={date}
					onNavigate={(newDate:Date) => {
						setDate(newDate);
						setValueFilters("yearPicker",newDate);
					}}
					onRangeChange={changeRangeHandle}
					selectable
					popup={false}
					doShowMoreDrillDown={false}
					onShowMore={(e: any, dateEvent: Date) => {setModalEvents({date:dateEvent,events:e});refModalEvents.current?.openModal();}}
					components={{
						eventWrapper:(e:any) => {
							const {event} = e;
							const traceabilityMessage = getTraceabilityMessage(event);
							return(
								<EventContainer type={event?.eventType} onClick={() => openEventModal(event)}>
									<div className="content">
										{ (event.responsibleName && !myActions) && 
														<Photo 
															name={event.responsibleName}
															link={event.responsibleImage && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${event.responsibleImage}`}
															hover
														/> 
										}
										<div className="text-wrapper">
											<OverflowSpan title={event?.title}>
												{event?.title}
											</OverflowSpan>
										
											{traceabilityMessage && (
												<RCCBadge 
													color="grey" 
													styleInline={css`
																		padding: 1px 5px; 
																		font-size: 0.7rem;
																	`}>
													{traceabilityMessage}
												</RCCBadge>
											)}
										</div>
									</div>
								</EventContainer>
							);
						},
						agenda: {
							event: (e:any) => { 
								const {event} = e;
								const traceabilityMessage = getTraceabilityMessage(event);
								return(
									<AgendaContainer type={event?.eventType} onClick={() => openEventModal(event)}>
										<OverflowSpan title={event?.title} style={{fontSize:"unset",marginLeft:"5px"}}>
											{event?.title}
										</OverflowSpan>
							
										{traceabilityMessage && (
											<RCCBadge 
												color="grey" 
												styleInline={css`
													padding: 1px 5px; 
													font-size: 0.7rem;
												`}>
												{traceabilityMessage}
											</RCCBadge>
										)}
									</AgendaContainer>
								);
							}
						}
					}}
				/>
				<Modal
					ref={refModalEvents}
					footer={{
						showSaveButton: false,
						cancelButtonText: translate("Fechar")
					}}
					header={{
						title:translate("Eventos"),
						subtitle:`${translate("Data")}: ${formatDate(modalEvents?.date as Date,currentLanguage)}`,
						icon:{name:"calendar"}
					}}
					styleInline={`${ModalSide}`}
				>
					<List 
						data={modalEvents?.events||[]}
						filter={() => true}
						onClickLi={(event:any) => openEventModal(event)}
						columns={[
							{
								idColumn: "title",
								nameColumn: translate("Titulo"),
								xs: 12,
								sm: 4,
								justify: "left",
								cellRenderer: colTitle
							},{
								idColumn: "responsible",
								nameColumn: translate("Responsável"),
								xs: 12,
								sm: 4,
								justify: "left",
								cellRenderer: colUser,
								hidden: !myActions ?[] :["xs","sm","md","lg","xl"]
							},{
								idColumn: "rastreability",
								nameColumn: translate("Rastreabilidade"),
								xs: 12,
								sm: 4,
								justify: "left",
								cellRenderer: colRastreability
							}]}
						showHeader
						styleInline="li.list-component-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }"
					/>
				</Modal>
				<ModalActions ref={refModalActions}/>
				<ModalOcurrences ref={refModalOcurrences} onCallbackUpdate={loadData}/>
				<ModalAudit ref={refModalAudit} onCallbackUpdate={loadData}/>
				<ModalFormMeeting ref={refModalMeeting}/>
			</RCCGrid>
		</>
	);
};

export default Calendar;
